import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import ReactPaginate from "react-paginate";
import _ from "lodash";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import Select from "react-select";

import { useDispatch, useSelector } from "react-redux";
import {
  fetchQuestions,
  deleteQuestion,
  fetchQuestionLetters,
} from "../../../store/reducers/questionsSectionSlice";

const buildDropdownOptions = (items = []) =>
  [...new Set(items.map((item) => `${item}`.trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
    .map((item) => ({
      label: item,
      value: item,
    }));

const QuestionSection = () => {
  const itemsPerPage = 10;
  const history = useHistory();
  const dispatch = useDispatch();

  const { questions, total, loading, questionLetters } =
    useSelector((state) => state.questions);

  const [currentPage, setCurrentPage] = useState(1);
  const [questionFilter, setQuestionFilter] = useState("");
  const [debouncedQuestion, setDebouncedQuestion] = useState("");
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);

  const letterOptions = buildDropdownOptions(questionLetters || []);

  useEffect(() => {
    dispatch(fetchQuestionLetters());
  }, [dispatch]);

  /* ================= FETCH DATA ================= */

  const pageCount = Math.ceil(total / itemsPerPage);
  const loadQuestions = (pageNo = 1, search = "", letter = "") => {
    const normalizedSearch = search.trim();

    dispatch(
      fetchQuestions({
        page: pageNo,
        limit: itemsPerPage,
        search: normalizedSearch,
        letter: letter,
        key: "all",
      })
    );
  };

  useEffect(() => {
    loadQuestions(currentPage, debouncedQuestion, selectedLetter?.value || "");
  }, [currentPage, debouncedQuestion, selectedLetter]);

  /* ================= SEARCH DEBOUNCE ================= */

  useEffect(() => {
    const handler = _.debounce((value) => {
      setDebouncedQuestion(value);
      setCurrentPage(1);
    }, 500);

    handler(questionFilter);

    return () => handler.cancel();
  }, [questionFilter]);

  /* ================= PAGINATION ================= */

  const handlePageClick = (event) => {
    const selectedPage = event.selected + 1;
    setCurrentPage(selectedPage);
  };

  /* ================= SEARCH ================= */

  const handleQuestionFilterChange = (e) => {
    setQuestionFilter(e.target.value);
  };

  const handleLetterChange = (selectedOption) => {
    setSelectedLetter(selectedOption);
    setCurrentPage(1);
  };

  /* ================= ADD ================= */

  const handelAddQuestion = () => {
    history.push("/admin-manage-question");
  };

  /* ================= DELETE ================= */
  const handleDeleteClick = (id) => {
    setSelectedQuestionId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deleteQuestion(selectedQuestionId)).unwrap();

      toast.success("Question deleted successfully");

      setShowDeleteModal(false);
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  return (
    <>
      {/* HEADER */}
      <div className="row my-2 align-items-center">
        <div className="col-12 d-flex flex-column flex-md-row align-items-center justify-content-between">
          <div className="fw-bold mb-2 mb-md-0" style={{ minWidth: 120 }}>
            Question Section
          </div>

          {/* SEARCH */}
          <div
            className="d-flex justify-content-center align-items-center gap-2 mx-auto"
            style={{ maxWidth: 760, width: "100%" }}
          >
            <div style={{ minWidth: 220, width: "100%" }}>
              <Select
                isClearable
                options={letterOptions}
                value={selectedLetter}
                onChange={handleLetterChange}
                placeholder="Search by Letter"
              />
            </div>

            <input
              type="text"
              className="form-control"
              style={{ minWidth: 300 }}
              placeholder="Search by Questions and Tags"
              value={questionFilter}
              onChange={handleQuestionFilterChange}
            />

            {(questionFilter || selectedLetter) && (
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => {
                  setQuestionFilter("");
                  setSelectedLetter(null);
                  setCurrentPage(1);
                }}
              >
                Clear
              </button>
            )}
          </div>

          <button
            className="btn bg_color"
            type="button"
            onClick={handelAddQuestion}
          >
            Add Question
          </button>
        </div>
      </div>

      {/* TABLE */}

      <table className="table align-middle">
        <thead>
          <tr>
            <th>Sr. No</th>
            <th>Question Id</th>
            <th>Question</th>
            <th>Status</th>
            <th style={{ width: "70px", textAlign: "center" }}>Edit</th>
            <th style={{ width: "80px", textAlign: "center" }}>Remove</th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <td colSpan="6" className="text-center">
                Loading...
              </td>
            </tr>
          ) : (
            questions?.map((item, i) => (
              <tr key={item.id}>
                <td>{i + 1 + itemsPerPage * (currentPage - 1)}</td>

                <td>{item.id}</td>

                <td>{item.question}</td>

                <td>
                  {item.active ? (
                    <span className="text-success fw-semibold">Active</span>
                  ) : (
                    <span className="text-danger fw-semibold">Inactive</span>
                  )}
                </td>

                {/* EDIT */}

                <td className="text-center">
                  <button
                    className="btn btn-sm bg_color"
                    onClick={() =>
                      history.push({
                        pathname: "/admin-manage-question",
                        search: `?id=${item.id}`,
                      })
                    }
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                </td>

                {/* DELETE */}

                <td className="text-center">
                  <button
                    className="btn btn-sm bg_color"
                    onClick={() => handleDeleteClick(item.id)}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))
          )}

          <tr hidden={questions?.length !== 0 || loading}>
            <td colSpan="6" className="text-center">
              Data Not Found
            </td>
          </tr>
        </tbody>
      </table>

      {/* PAGINATION */}

      <ReactPaginate
        nextLabel="Next"
        previousLabel="Previous"
        onPageChange={handlePageClick}
        pageCount={pageCount}
        forcePage={currentPage - 1}
        pageRangeDisplayed={5}
        pageClassName="page-item"
        pageLinkClassName="page-link"
        previousClassName="page-item"
        previousLinkClassName="page-link"
        nextClassName="page-item"
        nextLinkClassName="page-link"
        breakLabel="..."
        breakClassName="page-item"
        breakLinkClassName="page-link"
        containerClassName="pagination"
        activeClassName="active"
      />
      {/* DELETE MODAL */}

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>

        <Modal.Body>Are you sure you want to delete this question?</Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            No
          </Button>

          <Button variant="danger" onClick={handleConfirmDelete}>
            Yes
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default QuestionSection;
